import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Me } from '@teo/shared';
import { api, ApiRequestError } from '../api/client';
import i18n, { changeLanguage, isLanguage, type Language } from '../i18n';

interface AuthState {
  user: Me | null;
  /** true mientras se comprueba la sesión al cargar la aplicación. */
  loading: boolean;
  login: (user: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Cambia el idioma y, si hay sesión, lo guarda en el perfil del usuario. */
  setLanguage: (lang: Language) => Promise<void>;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback(async (me: Me | null) => {
    setUser(me);
    if (me && isLanguage(me.preferred_language) && me.preferred_language !== i18n.resolvedLanguage) {
      await changeLanguage(me.preferred_language);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      await applyUser(await api.me());
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 401) setUser(null);
      else throw e;
    }
  }, [applyUser]);

  useEffect(() => {
    refresh()
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(
    async (user: string, password: string) => {
      const me = await api.login(user, password);
      await applyUser(me);
      return me;
    },
    [applyUser],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const setLanguage = useCallback(
    async (lang: Language) => {
      await changeLanguage(lang);
      if (user) {
        await api.setPreferences(lang);
        setUser({ ...user, preferred_language: lang });
      }
    },
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout, refresh, setLanguage, can: (p) => !!user?.permissions.includes(p) }),
    [user, loading, login, logout, refresh, setLanguage],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth requiere AuthProvider');
  return ctx;
}
